import React from "react";
import { MdDownload, MdSms } from "react-icons/md";
import { FaEye, FaPhoneAlt, FaPlusCircle, FaExclamationTriangle, FaMapMarkerAlt, FaMapSigns } from "react-icons/fa";
import { LuMessageCircleWarning } from "react-icons/lu";
import formatCustomDateWithTime from "../helpers/customDateFormatWithTime";
import { useNavigate } from "react-router-dom";

const EventDetailsCard = ({
  eventDetails,
  onClick,
  cardStyle = "",
  onDownloadReport,
  token_count,
  operator_count,
  handleShowTokenList,
  handleShowOperatorList,
  onManageVillages,
}) => {
  const navigate = useNavigate();
  const {
    event_date,
    event_type,
    organisation_name,
    organisation_mobile,
    registered_on,
    eventor_1,
    eventor_2,
    reminder_voice,
    reminder_sms,
    moi_confirmation_sms,
    event_id,
    result_file,
    is_closed,
  } = eventDetails;
  const eventors = [eventor_1, eventor_2].filter(Boolean).join(", ");

  const isEventClosed = () => {
    if (result_file) return true;
    if (is_closed === 1 || is_closed === true || is_closed === "1") return true;

    if (event_date) {
      const getLocalDateKey = (val) => {
        const date = val instanceof Date ? val : new Date(val);
        if (isNaN(date.getTime())) return "";
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      };
      const todayKey = getLocalDateKey(new Date());
      const eventKey = getLocalDateKey(event_date);
      if (eventKey && eventKey < todayKey) {
        return true;
      }
    }
    return false;
  };

  const isClosed = isEventClosed();

  return (
    <div
      onClick={() => onClick(eventDetails)}
      className={`${cardStyle} rounded-2xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:-translate-y-0.5 transition-all duration-300 cursor-pointer overflow-hidden bg-white`}
    >
      {/* Card Header with gradient */}
      <div className="bg-slate-900 border-b border-slate-800 px-5 py-4 flex justify-between items-center gap-3">
        <div className="min-w-0">
          <h3 className="text-white font-bold text-sm truncate">{event_type}</h3>
          <p className="mt-0.5 text-[10px] font-semibold text-slate-400">
            Event ID #{event_id}
          </p>
        </div>
        {!isClosed && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/dashboard/create-token?event_id=${event_id}`);
            }}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold rounded-lg transition shadow-sm"
            title="Create Token for this event"
          >
            <FaPlusCircle />
            <span>Create Token</span>
          </button>
        )}
      </div>

      {/* Card Body */}
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-y-2 text-xs">
          <span className="text-slate-500 font-medium">Organisation:</span>
          <span className="text-slate-700 font-semibold flex items-center gap-2 min-w-0">
            <span className="truncate">{organisation_name}</span>
            {organisation_mobile && (
              <a
                href={`tel:${organisation_mobile}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 transition-all duration-200 text-[10px] font-semibold flex-shrink-0"
                title={`Call ${organisation_name}`}
              >
                <FaPhoneAlt size={8} />
                <span>Call</span>
              </a>
            )}
          </span>

          <span className="text-slate-500 font-medium">Event Date:</span>
          <span className="text-slate-700 font-semibold">
            {formatCustomDateWithTime(event_date)}
          </span>

          <span className="text-slate-500 font-medium">Eventors:</span>
          <span className="text-slate-700 font-semibold truncate">{eventors || "—"}</span>

          <span className="text-slate-500 font-medium">Registered on:</span>
          <span className="text-slate-700">
            {formatCustomDateWithTime(registered_on)}
          </span>
        </div>

        {/* Activated services (if any) */}
        {reminder_voice || reminder_sms || moi_confirmation_sms ? (
          <div className="flex justify-between items-center pt-2.5 border-t border-slate-100">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Active Services</span>
            <div className="flex gap-2">
              {reminder_voice ? (
                <span title="Voice Reminder" className="p-1 bg-slate-50 border border-slate-100 rounded text-slate-600">
                  <FaPhoneAlt size={10} />
                </span>
              ) : null}
              {reminder_sms ? (
                <span title="SMS Reminder" className="p-1 bg-slate-50 border border-slate-100 rounded text-slate-600">
                  <LuMessageCircleWarning size={10} />
                </span>
              ) : null}
              {moi_confirmation_sms ? (
                <span title="Confirmation SMS" className="p-1 bg-slate-50 border border-slate-100 rounded text-slate-600">
                  <MdSms size={10} />
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center pt-2.5 border-t border-slate-100">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Active Services</span>
            <span className="text-[10px] text-slate-400 italic">No active services</span>
          </div>
        )}
      </div>

      {/* Card Footer – Action Buttons Row */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-slate-50/50 border-t border-slate-100">
        {!isClosed && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleShowTokenList(e, event_id);
              }}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-lg transition bg-slate-100 hover:bg-slate-200/80 text-slate-700"
            >
              <FaEye className="text-xs" />
              <span>Tokens ({token_count})</span>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleShowOperatorList(e, event_id);
              }}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-lg transition bg-slate-100 hover:bg-slate-200/80 text-slate-700"
            >
              <FaEye className="text-xs" />
              <span>Operators ({operator_count})</span>
            </button>

            {onManageVillages && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onManageVillages(event_id);
                }}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-lg transition bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-100"
              >
                <FaMapSigns className="text-xs" />
                <span>Villages</span>
              </button>
            )}
          </>
        )}

        {result_file && onDownloadReport && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDownloadReport(result_file);
            }}
            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold rounded-lg transition bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
          >
            <MdDownload className="text-xs" />
            <span>Report</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default EventDetailsCard;
